import styled from '@/styles';

export const Layout = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '20px',
  alignItems: 'flex-start',
});

export const Card = styled('section', {
  backgroundColor: '#FFFFFF',
  border: '1px solid #DADADA',
  borderRadius: '8px',
});

export const ListCard = styled(Card, {
  flex: '1 1 280px',
  maxWidth: '100%',
  padding: '12px',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  maxHeight: '1180px',
  overflow: 'auto',
});

export const EditorCard = styled(Card, {
  flex: '999 1 620px',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
});

export const GroupTitle = styled('h3', {
  margin: 0,
  fontSize: '12px',
  fontWeight: 700,
  color: '#5A5A5A',
  textTransform: 'uppercase',
  letterSpacing: '.03em',
  padding: '10px 10px 4px',
});

export const Item = styled('button', {
  font: 'inherit',
  textAlign: 'left',
  width: '100%',
  border: 'none',
  background: 'none',
  borderRadius: '6px',
  padding: '8px 10px',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  color: '#1E1E1E',
  '&:hover': { backgroundColor: '#F4F5F7' },
  '&[aria-current="true"]': {
    backgroundColor: '#E7F4E6',
    boxShadow: 'inset 3px 0 0 #1E8E3E',
  },
});

export const Row = styled('span', {
  display: 'flex',
  gap: '6px',
  alignItems: 'center',
  flexWrap: 'wrap',
});

export const Caption = styled('span', {
  color: '#6B6B6B',
  fontSize: '13px',
});

export const Label = styled('span', {
  color: '#3D3D3D',
  fontSize: '13px',
  fontWeight: 600,
});

export const Segment = styled('div', {
  display: 'inline-flex',
  flexWrap: 'wrap',
  border: '1px solid #DADADA',
  borderRadius: '8px',
  overflow: 'hidden',
  backgroundColor: '#FFFFFF',
  '& button': {
    font: 'inherit',
    fontSize: '13px',
    border: 'none',
    borderRight: '1px solid #DADADA',
    backgroundColor: '#FFFFFF',
    padding: '7px 12px',
    cursor: 'pointer',
    color: '#3D3D3D',
    minHeight: '34px',
  },
  '& button:last-child': { borderRight: 'none' },
  '& button[aria-pressed="true"]': {
    backgroundColor: '#1E8E3E',
    color: '#FFFFFF',
    fontWeight: 600,
  },
});

export const TextArea = styled('textarea', {
  font: 'inherit',
  fontSize: '14px',
  lineHeight: 1.45,
  border: '1px solid #BDBDBD',
  borderRadius: '6px',
  padding: '9px 11px',
  backgroundColor: '#FFFFFF',
  color: '#1E1E1E',
  width: '100%',
  resize: 'vertical',
  '&[readonly]': { backgroundColor: '#F7F7F7' },
  variants: {
    invalid: {
      true: { borderColor: '#CF040C', boxShadow: '0 0 0 1px #CF040C' },
    },
  },
});

export const Chip = styled('button', {
  font: 'inherit',
  fontSize: '12px',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  border: '1px solid #CFE6D3',
  backgroundColor: '#F6FBF6',
  color: '#17722F',
  borderRadius: '6px',
  padding: '3px 7px',
  cursor: 'pointer',
  '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
});

export const Locked = styled('div', {
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  backgroundColor: '#F4F4F4',
  border: '1px dashed #BDBDBD',
  borderRadius: '6px',
  padding: '7px 10px',
  fontSize: '13px',
  color: '#4A4A4A',
});

export const Notice = styled('div', {
  borderRadius: '6px',
  padding: '10px 12px',
  fontSize: '14px',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  variants: {
    tone: {
      green: {
        backgroundColor: '#F6FBF6',
        border: '1px solid #CFE6D3',
        color: '#1E1E1E',
      },
      red: {
        backgroundColor: '#FFF6F4',
        border: '1px solid #F3B4AA',
        color: '#8A0208',
      },
      done: { backgroundColor: '#E7F4E6', color: '#17722F' },
    },
  },
});

export const Phone = styled('div', {
  backgroundColor: '#EEF0F3',
  borderRadius: '12px',
  padding: '14px',
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
});

export const Bubble = styled('div', {
  alignSelf: 'flex-start',
  maxWidth: '92%',
  backgroundColor: '#FFFFFF',
  borderRadius: '14px 14px 14px 4px',
  padding: '10px 12px',
  lineHeight: 1.4,
  boxShadow: '0 1px 1px rgba(0,0,0,.06)',
  whiteSpace: 'pre-line',
});

export const TabStrip = styled('div', {
  display: 'flex',
  padding: '0 20px',
  borderBottom: '1px solid #EDEDED',
  '& button': {
    font: 'inherit',
    fontSize: '14px',
    fontWeight: 600,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    padding: '12px 2px',
    marginRight: '22px',
    color: '#5A5A5A',
    borderBottom: '3px solid transparent',
  },
  '& button[aria-selected="true"]': {
    color: '#1E1E1E',
    borderBottomColor: '#1E8E3E',
  },
});

export const Header = styled('div', {
  padding: '16px 20px',
  borderBottom: '1px solid #EDEDED',
  display: 'flex',
  flexDirection: 'column',
  gap: '3px',
});

export const Title = styled('h2', { margin: 0, fontSize: '18px' });

export const Body = styled('div', {
  padding: '16px 20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

export const WrapRow = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  alignItems: 'center',
});

export const PlainText = styled('span', {
  color: '#3D3D3D',
  fontSize: '13px',
});

export const SmallText = styled('span', { fontSize: '13px' });

export const VariantBox = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
});

export const Columns = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '20px',
  alignItems: 'flex-start',
});

export const FieldsColumn = styled('div', {
  flex: '1 1 380px',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

export const PreviewColumn = styled('div', {
  flex: '1 1 320px',
  minWidth: 0,
});

export const Footer = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '10px',
  alignItems: 'center',
  borderTop: '1px solid #EDEDED',
  paddingTop: '14px',
});

export const FooterActions = styled('div', {
  display: 'flex',
  gap: '10px',
  marginRight: 'auto',
});

export const Unsaved = styled('span', {
  color: '#7A5600',
  fontSize: '13px',
  fontWeight: 600,
});
